import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import { CollapsePanelProps } from "antd/es/collapse";
import Form from "antd/es/form";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { Button, CardCollapse, CollapsePanel } from "@mapmycustomers/ui";

import { getFunnelStages } from "@app/store/deal";
import { createStage, deleteFunnel, updateFunnel, updateStages } from "@app/store/deal/actions";
import { getColors } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";

import DealStage from "../DealStage";
import FunnelHeader from "../FunnelHeader";

import styles from "./DealFunnel.module.scss";
import messages from "./messages";

interface OwnProps extends Omit<CollapsePanelProps, "extra" | "header"> {
  funnel: Funnel;
  highlighted?: boolean;
  isActiveFunnel?: boolean;
}

interface Props extends OwnProps {
  colors: Color[];
  createStage: typeof createStage.request;
  deleteFunnel: typeof deleteFunnel.request;
  onHide: () => void;
  stages: Stage[];
  updateFunnel: typeof updateFunnel.request;
  updateStages: typeof updateStages.request;
}

const DealFunnel: React.FC<Props> = ({
  colors,
  createStage,
  deleteFunnel,
  funnel,
  highlighted,
  isActiveFunnel,
  onHide,
  stages,
  updateFunnel,
  updateStages,
  ...panelProps
}) => {
  const intl = useIntl();

  const [funnelForm] = Form.useForm();
  const [stageForm] = Form.useForm();
  const [nameEditing, setNameEditing] = useState(false);

  const handleSave = useCallback(() => {
    const updatedFunnel = { ...funnel };
    const formFunnel = funnelForm.getFieldsValue();
    updatedFunnel.name = formFunnel.name;
    updateFunnel({ funnel: updatedFunnel });
    setNameEditing(false);

    stageForm.validateFields().then(() => {
      const stageList = stageForm.getFieldsValue();
      updateStages({
        funnelId: funnel.id,
        stages: stageList.stages.map((stage: Stage) => ({
          ...stage,
          weight: stage.weight >= 1 ? stage.weight / 100 : stage.weight,
        })),
      });
    });
  }, [funnel, funnelForm, stageForm, updateFunnel, updateStages]);

  const funnelElementId = useMemo(() => `funnel-${funnel.id}`, [funnel]);

  useEffect(() => {
    if (highlighted) {
      const element = document.getElementById(funnelElementId);
      if (element && element instanceof HTMLElement) {
        element.scrollIntoView({ block: "start" });
      }
    }
  }, [funnelElementId, highlighted]);

  const handleToggleNameEditing = useCallback(
    (editing: boolean) => {
      funnelForm.resetFields();
      setNameEditing(editing);
    },
    [funnelForm]
  );

  const handleAddStage = useCallback(() => {
    stageForm.validateFields().then(() => {
      handleSave();

      const existingStagesColors = new Set(stages.map(({ color }) => color));
      const suggestedColor = colors.find((color) => !existingStagesColors.has(color));
      if (suggestedColor) {
        const stageAdding = {
          color: suggestedColor,
          displayOrder: 0,
          name: "",
          weight: 0,
        };
        createStage({ funnelId: funnel.id, stage: stageAdding });
      }
    });
  }, [createStage, funnel.id, stages, colors, handleSave, stageForm]);

  // Allow only unique colors for each stage, so no sense to allow adding stages once we're using entire color palette
  const canAddStage = useMemo(() => stages.length < colors.length, [stages, colors]);

  const handleDrop = useCallback(
    (updatedStages: Stage[]) => {
      updateStages({
        funnelId: funnel.id,
        stages: updatedStages.map((stage: Stage) => ({
          ...stage,
          weight: stage.weight > 1 ? stage.weight / 100 : stage.weight,
        })),
      });
    },
    [funnel, updateStages]
  );

  useEffect(() => {
    funnelForm.setFieldsValue({ name: funnel.name });
  }, [funnelForm, funnel.name]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Form form={funnelForm} initialValues={funnel} name={`funnel_${funnel.id}`}>
          <FunnelHeader
            editing={nameEditing}
            funnel={funnel}
            onDelete={deleteFunnel}
            onToggleEditing={handleToggleNameEditing}
          />
        </Form>
      </div>
      <CardCollapse
        accordion
        className={styles.collapse}
        defaultActiveKey={highlighted ? funnel.id : undefined}
        size="small"
      >
        <CollapsePanel
          {...panelProps}
          header={
            <span onClick={nameEditing ? () => handleToggleNameEditing(!nameEditing) : undefined}>
              {intl.formatMessage(messages.subtitle, { count: stages.length })}
            </span>
          }
          id={funnelElementId}
          key={funnel.id}
        >
          <Row className={styles.rowHeaders} gutter={[layout.spacerS, layout.spacerS]}>
            <Col className={styles.iconColumn} span={1}>
              <Tooltip placement="bottomLeft" title={intl.formatMessage(messages.tooltip)}>
                <FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
              </Tooltip>
            </Col>
            <Col span={12}>{intl.formatMessage(messages.title)}</Col>
            <Col span={3}> {intl.formatMessage(messages.probability)}</Col>
            <Col span={3}>{intl.formatMessage(messages.color)}</Col>
            <Col span={4}>
              {intl.formatMessage(messages.label)}{" "}
              <Tooltip placement="bottomRight" title={intl.formatMessage(messages.rottingAt)}>
                <FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
              </Tooltip>
            </Col>
          </Row>
          <DealStage
            funnelId={funnel.id}
            onDrop={handleDrop}
            stageForm={stageForm}
            stages={stages}
          />
          <Button disabled={!canAddStage} onClick={handleAddStage} type="text">
            {intl.formatMessage(messages.addStage)}
          </Button>
        </CollapsePanel>
      </CardCollapse>
    </div>
  );
};

const mapStateToProps = (state: RootState, { funnel }: OwnProps) => ({
  colors: getColors(state)!,
  stages: getFunnelStages(state)[funnel.id] ?? [],
});

const mapDispatchToProps = {
  createStage: createStage.request,
  deleteFunnel: deleteFunnel.request,
  updateFunnel: updateFunnel.request,
  updateStages: updateStages.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealFunnel);
